import React, { useState, useEffect } from "react";
import { Box, Typography, Grid2 } from "@mui/material";
import FullScreenDialog from "../../../../Components/FullScreenDialog";
import CloseButton from "../../../../Components/CloseButton";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { useTranslation } from "react-i18next";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import { formatDateRange, filterTableColumnsConfigForSeverity } from "../../../../Helper/Analytics/AnalyticsHelper";
import useAnalyticsBmsFaultDrilldown from "./hooks/useAnalyticsBmsFaultDrilldown";
import { useBaseEntityInfo } from "../../../../Hooks/usePagesContent";
import { useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";

// ─── Meta key mapping ─────────────────────────────────────────────────────────
const META_KEY_MAP = {
  triggeredFaultBreakdown: "bmsFaults.triggeredFaultBreakdown.drilldownTable",
  resolutionDurationBreakdown: "bmsFaults.resolutionDurationBreakdown.drilldownTable",
};

const CHART_DISPLAY_NAMES = {
  triggeredFaultBreakdown: "Triggered Battery System Fault Breakdown",
  resolutionDurationBreakdown: "Battery System Fault Avg. Resolution Duration Breakdown",
};

const TABLE_ID = "BmsFaultDrillDown";
const PAGE_SIZE = 20;

// ─── Modal Header ─────────────────────────────────────────────────────────────
const DrillDownHeader = ({ chartName, faultName, entityLabel, entityId, severityName, startDate, endDate, onClose }) => {
  const chartDisplayName = CHART_DISPLAY_NAMES[chartName] || "BMS Fault Breakdown";

  return (
    <Grid2 container justifyContent="space-between" alignItems="flex-start" py={2} gap={2}>
      {/* Row 1: "Drill Down:" + breadcrumb + close button */}
      <Grid2
        container
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
        sx={{ borderBottom: "1px solid #E6E6E6", px: 3, pb: 2 }}
      >
        <Grid2 container flexDirection="column" gap={0.5}>
          <Typography variant="fw700s20c000000" sx={{ lineHeight: "28px" }}>
            Drill Down:
          </Typography>

          {/* Breadcrumb subtitle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px" }}>
              {chartDisplayName}
            </Typography>

            {faultName && (
              <>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px", color: "#BDBDBD" }}>
                  ›
                </Typography>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px" }}>
                  {faultName}
                </Typography>
              </>
            )}

            {severityName && chartName === "triggeredFaultBreakdown" && (
              <>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px", color: "#BDBDBD" }}>
                  ›
                </Typography>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px" }}>
                  {severityName}
                </Typography>
              </>
            )}

            {entityId && (
              <>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px", color: "#BDBDBD" }}>
                  ·
                </Typography>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px" }}>
                  {entityLabel}: {entityId}
                </Typography>
              </>
            )}

            {startDate && endDate && (
              <>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px", color: "#BDBDBD" }}>
                  ·
                </Typography>
                <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "24px" }}>
                  {formatDateRange(startDate, endDate)}
                </Typography>
              </>
            )}
          </Box>
        </Grid2>

        <Grid2 container justifyContent="center" alignItems="center">
          <CloseButton onClick={onClose} />
        </Grid2>
      </Grid2>

      {/* Row 2: "Summary" label */}
      <Grid2 container width="100%" sx={{ px: 3 }}>
        <Typography variant="fw500s18lh24c000000">Summary</Typography>
      </Grid2>
    </Grid2>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * BmsFaultDrillDownModal
 *
 * Full-screen drilldown modal for the two DigitalTwin BMS Fault charts.
 * Table columns + filters are driven by page meta (no hardcoding).
 * Data is fetched from /api/diagnostics/v2/incidents/{batteryID} — the same
 * endpoint used by BatteryBmsFaults — via useBmsFaultDrilldown hook.
 *
 * Props:
 *   open            – boolean
 *   onClose         – () => void
 *   chartName       – "triggeredFaults" | "resolvedFaults"
 *   faultName       – string  (incidentErrorName from clicked bar)
 *   batteryID       – string
 *   resolutionType  – "auto" | "manual" | null  (resolvedFaults only)
 *   startDate       – string  (YYYY-MM-DD, from chart date picker)
 *   endDate         – string  (YYYY-MM-DD)
 *   pageContent     – object  (from usePagesContent())
 *   customRenderers – object  (action column renderer from parent)
 */
const BmsFaultDrillDownModal = ({
  open,
  onClose,
  chartName,
  faultName,
  batteryID,
  resolutionType,
  severityName,
  startDate,
  endDate,
  pageContent,
  customRenderers = {},
}) => {
  const { t } = useTranslation(["common", "generic_table"]);

  // ── Entity label (vehicle vs battery) ────────────────────────────────────────
  const { baseEntityType, baseEntityTypeIDLable } = useBaseEntityInfo();
  const vehicleInfo = useVehicleInfo();
  const entityLabel = baseEntityTypeIDLable;
  const entityId = baseEntityType === "vehicle"
    ? (vehicleInfo?.licensePlateNo || batteryID)
    : batteryID;

  // ── Column state (meta-driven, local to modal) ────────────────────────────────
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
  const [currentTableColumns, setCurrentTableColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);

  // ── Meta resolution ─────────────────────────────────────────────────────────
  const metaKey = META_KEY_MAP[chartName] || META_KEY_MAP.triggeredFaultBreakdown;
  const tableMetaConfig = pageContent?.metaInfo?.table?.[metaKey];

  // ── Data hook ─────────────────────────────────────────────────────────────────
  const {
    tableData,
    pageInfo,
    isLoading,
    pageNumber,
    onPageChange,
    isSelectAll,
    onSelectChange,
    onSelectAllChange,
    getTotalRowSelection,
    getTotalRowSelectionCount,
    getCurrentPageSelection,
    getCSVData,
    getCSVFileName,
    getCsvColumnParams,
  } = useAnalyticsBmsFaultDrilldown({
    batteryID,
    faultName,
    chartName,
    resolutionType,
    severityName,
    startDate,
    endDate,
    open,
    currentTableColumns,
    columnsConfig: tableMetaConfig?.columnsConfig,
  });

  // ── Setup table columns from meta when modal opens ───────────────────────────
  useEffect(() => {
    if (!open || !tableMetaConfig) return;

    const rawTableColumns = tableMetaConfig.columnsConfig || {};
    const tableColumns =
      chartName === "triggeredFaultBreakdown" && severityName
        ? filterTableColumnsConfigForSeverity(rawTableColumns, severityName)
        : rawTableColumns;
    const tableColumnOrder = tableMetaConfig.columnOrdering || [];

    const allColumns = getColumnsConfig({
      tableColumns,
      tableColumnOrder,
      customRenderers,
      t,
    });

    setAvailableColumns(allColumns);

    const initialSelected = tableColumnOrder.slice(0, MAX_COLUMN_SELECTION_COUNT);
    setSelectedColumns(initialSelected);
    setDefaultSelectedColumns(initialSelected);

    const visible = allColumns.filter((col) => initialSelected.includes(col.key));
    setCurrentTableColumns(visible);
    setColumnOrder(visible.map((col) => col.headerLabel));
  }, [open, tableMetaConfig, chartName, severityName]); // customRenderers + t are stable

  // ── Sync currentTableColumns when user changes visible columns ───────────────
  useEffect(() => {
    if (!availableColumns.length) return;
    const visible = availableColumns.filter((col) => selectedColumns.includes(col.key));
    setCurrentTableColumns(visible);
  }, [selectedColumns, availableColumns]);

  // ── Guard: no table config in meta → render nothing ──────────────────────────
  // Must stay after all hook calls (hooks cannot be called conditionally).
  if (!tableMetaConfig) return null;

  return (
    <FullScreenDialog
      open={open}
      handleClose={onClose}
      containerStyles={{ bgcolor: "#FAFAFA", overflowY: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: "#FAFAFA",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <DrillDownHeader
          chartName={chartName}
          faultName={faultName}
          entityLabel={entityLabel}
          entityId={entityId}
          severityName={severityName}
          startDate={startDate}
          endDate={endDate}
          onClose={onClose}
        />

        {/* ── Body ── */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 3,
            pb: 2,
            minHeight: 0,
          }}
        >
          <GenericDraggableTableView
            tableID={TABLE_ID}
            title="Breakdown"
            count={pageInfo?.total}
            filteredCount={false}
            showPrimaryButton={false}
            disableSearch={true}
            // Columns
            tableColumns={currentTableColumns}
            initialColumns={availableColumns}
            selectedColumns={selectedColumns}
            applySelectedColumns={(cols) => setSelectedColumns(cols)}
            onColumnOrderChange={(cols) =>
              setColumnOrder(cols.map((c) => c.headerLabel))
            }
            columnOrder={columnOrder}
            maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
            defaultSelectedColumns={defaultSelectedColumns}
            // Data
            tableData={tableData}
            isLoading={isLoading}
            emptyMessage={
              isLoading
                ? "Loading..."
                : "No data found for this fault in the selected period"
            }
            // Pagination
            pageNumber={pageNumber}
            total={pageInfo?.total}
            rowsPerPage={PAGE_SIZE}
            onPageChange={onPageChange}
            // Row selection (checkboxes)
            rowSelection={{
              selectedRowKeys: getCurrentPageSelection(),
              onSelect: onSelectChange,
              onSelectAll: onSelectAllChange,
            }}
            getTotalRowSelection={getTotalRowSelection}
            getTotalRowSelectionCount={getTotalRowSelectionCount}
            // CSV download
            getCSVData={getCSVData}
            getCSVFileName={getCSVFileName}
            getCsvColumnSettings={getCsvColumnParams}
          />
        </Box>
      </Box>
    </FullScreenDialog>
  );
};

export default BmsFaultDrillDownModal;
